import React, { useState, useEffect, useContext } from 'react';
import { ThemeProvider } from 'styled-components';
import { useIdleTimer } from 'react-idle-timer';
import { BrowserRouter as Router, Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { StylesProvider } from '@mui/styles';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { GlobalContext } from '../context/GobalState';
import useToken from '../hooks/useToken';
import { logoutUser } from '../api/api';

import Login from 'components/organisms/Login/Login';
import PasswordChange from 'components/organisms/PasswordChange/PasswordChange';
import ItemsList from 'components/organisms/ItemsList/ItemsList';
import ErrorBoundary from 'components/organisms/ErrorBoundary/ErrorBoundary';
import MainTemplate from 'components/templates/MainTemplate/MainTemplate';
import SideDrawer from 'components/organisms/SideDrawer/SideDrawer';
import ImportView from 'components/organisms/ImportView/ImportView';

import { GlobalStyle } from 'assets/styles/globalStyles/GlobalStyle';
import { theme } from 'assets/styles/globalStyles/theme';
import Wrapper from './Root.styles';
import axios from 'axios';
import { api } from '../api/api';

import favicon from 'assets/images/vee_FAV.png';
import Spinner from 'components/molecules/Spinner/Spinner';
import OAuthCallback from 'components/organisms/OauthCallback/OAuthCallback';
import NoPermission from 'components/organisms/NoPermission/NoPermission';
import { useHistory } from 'react-router-dom';
import { config } from '@fortawesome/fontawesome-svg-core';

//Główny komponent Rootowy
const Root = () => {
  config.autoAddCss = false;

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const { token, setToken } = useToken();
  const { setAuthToken, setPermissionLevel, setEnvironment, setSkill, environment, setCampaign, authToken, skill } =
    useContext(GlobalContext);

  const [queryParams, setQueryParams] = useState({ state: '', sessionState: '', code: '' });
  const history = useHistory();

  const location = useLocation();
  const shouldShowSideDrawerOrNavigation = location.pathname !== '/no-permission';

  useEffect(() => {
    // Get the full URL's query string
    const searchParams = new URLSearchParams(window.location.search);

    // Set parameters in state
    setQueryParams({
      state: searchParams.get('state'),
      sessionState: searchParams.get('session_state'),
      code: searchParams.get('code'),
    });
  }, []); // Runs once on mount
  const handleOnIdle = () => {
    if (isLoggedIn) {
      logout();
    }
  };

  useIdleTimer({
    timeout: 1000 * 60 * 15,
    onIdle: handleOnIdle,
  });

  useEffect(() => {
    setEnvironment(process.env.ENVIRONMENT);
  }, [setEnvironment, setSkill, environment, setCampaign]);

  useEffect(() => {
    if (token?.token) {
      setIsLoggedIn(true);
      setForcePasswordChange && setForcePasswordChange(token.force_password_change);
      setPermissionLevel && setPermissionLevel(token.permitted_panel);
      setAuthToken && setAuthToken(token.token);
    } else {
      setIsLoggedIn(false);
    }
  }, [token, setAuthToken, setPermissionLevel]);

  const logout = () => {
    logoutUser(token.token);
    setToken('');
    setIsLoggedIn(false);
    history.replace('/items-list');
  };

  if (!isLoggedIn) {
    if (!queryParams.code && !queryParams.state && !queryParams.sessionState) {
      return (
        <HelmetProvider>
          <ThemeProvider theme={theme}>
            <Helmet link={[{ rel: 'icon', type: 'image/png', href: favicon }]} />
            <Login setToken={setToken} />
          </ThemeProvider>
        </HelmetProvider>
      );
    } else {
      return (
        <Router>
          <HelmetProvider>
            <ThemeProvider theme={theme}>
              <Helmet link={[{ rel: 'icon', type: 'image/png', href: favicon }]} />
              <OAuthCallback queryParams={queryParams} setToken={setToken} setQueryParams={setQueryParams} />
            </ThemeProvider>
          </HelmetProvider>
        </Router>
      );
    }
  }

  if (isLoggedIn && forcePasswordChange) {
    return (
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <Helmet link={[{ rel: 'icon', type: 'image/png', href: favicon }]} />
          <PasswordChange
            setForcePasswordChange={setForcePasswordChange}
            logout={logout}
            token={token}
            setToken={setToken}
          />
        </ThemeProvider>
      </HelmetProvider>
    );
  }

  return (
    <HelmetProvider>
      <StylesProvider injectFirst>
        <ThemeProvider theme={theme}>
          <ErrorBoundary setIsLoggedIn={setIsLoggedIn}>
            <Router>
              <GlobalStyle />
              <MainTemplate logout={logout} shouldShowSideDrawerOrNavigation={shouldShowSideDrawerOrNavigation}>
                <Wrapper>
                  {shouldShowSideDrawerOrNavigation && <SideDrawer />}
                  <Switch>
                    <Route exact path="/">
                      <Helmet link={[{ rel: 'icon', type: 'image/png', href: favicon }]} />
                      <Redirect to="/items-list" />
                    </Route>
                    <Route path="/items-list">
                      <ItemsList />
                    </Route>
                    <Route path="/importview">
                      <ImportView />
                    </Route>
                    <Route path="/no-permission">
                      <NoPermission logout={logout} />
                    </Route>
                  </Switch>
                </Wrapper>
              </MainTemplate>
            </Router>
          </ErrorBoundary>
        </ThemeProvider>
      </StylesProvider>
    </HelmetProvider>
  );
};

export default Root;
